// utils/analytics.js
//
// Cliente de telemetría anónima. Buffer en memoria + flush en batches a
// /api/analytics. Todo es best-effort y no bloqueante: la analítica nunca
// puede frenar, romper, ni filtrarse a la app real. Port 1:1 de
// assetto-gallery (src/analytics/client.ts + src/analytics/types.ts).
//
// Postura de privacidad (ver db/schema.js):
//  - El único identificador es `installId`, un UUID random en localStorage.
//    NO es el id de cuenta y nunca se manda junto a uno.
//  - Nada acá lee nombres, mails, ni texto libre. Los props son ids de
//    contenido, conteos y flags.
//  - Hay un switch de opt-out (localStorage `cs_analytics_optout`) que
//    silencia el cliente por completo — el gancho para un futuro toggle en
//    Settings sin necesitarlo hoy, ya que el dato recolectado es anónimo.

import { getBridge, isShell } from '@/utils/bridge'

const INSTALL_ID_KEY = 'cs_analytics_iid'
const OPTOUT_KEY = 'cs_analytics_optout'
const INGEST_URL = '/api/analytics'

// Ventana de debounce antes de un flush no urgente; eventos poco frecuentes
// (un launch) conviene mandarlos pronto, los frecuentes (vistas) se agrupan.
const FLUSH_DEBOUNCE_MS = 4000
// Tope duro del buffer en memoria para que un dispositivo offline no lo haga
// crecer para siempre.
const MAX_QUEUE = 400

/** Nombres canónicos de evento. Mantenerlos estables — los dashboards agrupan por esto.
 *
 *  app_open          — la webapp arrancó (una vez por corrida); lleva contexto device/locale
 *  app_close         — terminó la corrida (lleva duración; best-effort via pagehide)
 *  section_view      — se abrió una sección top-level (home/manager/wiki/blog/updates/export/editor)
 *  module_open       — se abrió un módulo del editor (crosshair/mouse/viewmodel/…)
 *  module_edit       — se editó un valor de un módulo ({module}) — dedupeado por módulo/sesión
 *  config_save       — se guardó el config activo
 *  config_export     — se exportó el .cfg (descarga)
 *  config_apply      — se aplicó el config al juego (instalar + sync)
 *  config_import     — se importó un config/código externo
 *  cs2_launch        — se lanzó CS2 desde la app
 *  preset_use        — se aplicó un preset/config de pro ({kind, id})
 *  configs_count     — tamaño de la biblioteca de configs del usuario ({n}), una vez por carga
 *  feature_use       — uso de una feature discreta ({feature}: tema, idioma…)
 *  ad_impression     — un slot de publicidad quedó visible ({slot})
 *  ad_click          — se clickeó un slot de publicidad ({slot})
 */

let queue = []
let flushTimer = null
let sessionId = ''
let shellVersion = null
let startedAt = 0
let initialized = false

function browser() {
    return typeof window !== 'undefined'
}

/** SO grueso desde el UA — perfil de audiencia, nunca fingerprinting. */
function detectOs(ua) {
    if (/Windows/i.test(ua)) return 'Windows'
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS'
    if (/Android/i.test(ua)) return 'Android'
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
    if (/Linux/i.test(ua)) return 'Linux'
    return 'otro'
}

/** Contexto anónimo device/locale mandado con app_open: idioma, timezone,
 *  tamaño de pantalla y SO. Señal estándar de perfil de audiencia, sin PII. */
function deviceContext() {
    const ctx = {}
    try {
        ctx.lang = navigator.language
        ctx.tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (window.screen) {
            ctx.sw = window.screen.width
            ctx.sh = window.screen.height
        }
        ctx.os = detectOs(navigator.userAgent)
    } catch {
        /* best-effort */
    }
    return ctx
}

/** Id de dispositivo anónimo y rotable. Se crea lazy y se reusa entre corridas. */
export function getInstallId() {
    if (!browser() || typeof localStorage === 'undefined') return 'ssr'
    let id = null
    try {
        id = localStorage.getItem(INSTALL_ID_KEY)
        if (!id) {
            id = crypto.randomUUID()
            localStorage.setItem(INSTALL_ID_KEY, id)
        }
    } catch {
        // Modo privado / storage deshabilitado — fallback a un id efímero para
        // que los eventos igual fluyan (solo no correlacionan entre reloads).
        id = id ?? 'ephemeral'
    }
    return id
}

/** True salvo que el usuario haya optado por salir. El dato anónimo está on por default. */
export function isTelemetryEnabled() {
    if (!browser()) return false
    try {
        return localStorage.getItem(OPTOUT_KEY) !== '1'
    } catch {
        return true
    }
}

/** Un futuro toggle en Settings puede llamar esto; nada en la UI lo hace todavía. */
export function setTelemetryOptOut(optOut) {
    if (!browser()) return
    try {
        if (optOut) localStorage.setItem(OPTOUT_KEY, '1')
        else localStorage.removeItem(OPTOUT_KEY)
    } catch {
        /* ignore */
    }
}

/** Registra un evento. Barato, síncrono, nunca tira. */
export function track(name, props) {
    if (!browser() || !isTelemetryEnabled()) return
    if (queue.length >= MAX_QUEUE) queue.shift() // descarta lo más viejo bajo offline sostenido
    queue.push({ name, props, ts: new Date().toISOString() })
    scheduleFlush()
}

function scheduleFlush() {
    if (flushTimer !== null) return
    flushTimer = setTimeout(() => {
        flushTimer = null
        void flush()
    }, FLUSH_DEBOUNCE_MS)
}

function buildPayload(events) {
    return {
        installId: getInstallId(),
        sessionId,
        platform: isShell() ? 'shell' : 'web',
        shellVersion,
        events,
    }
}

/** Manda todo lo bufferizado via fetch(keepalive). Re-encola si falla. */
async function flush() {
    if (!browser() || queue.length === 0) return
    const batch = queue
    queue = []
    try {
        const res = await fetch(INGEST_URL, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(buildPayload(batch)),
            keepalive: true,
        })
        if (!res.ok) throw new Error(String(res.status))
    } catch {
        // Vuelve a poner el batch en cola (acotado) para que una falla
        // transitoria no lo pierda.
        queue = [...batch, ...queue].slice(-MAX_QUEUE)
        scheduleFlush()
    }
}

/** Último flush antes de que la página se oculte — usa sendBeacon, que sobrevive al unload. */
function flushBeacon() {
    if (!browser() || queue.length === 0) return
    const batch = queue
    queue = []
    try {
        const blob = new Blob([JSON.stringify(buildPayload(batch))], { type: 'application/json' })
        const ok = typeof navigator !== 'undefined' && navigator.sendBeacon?.(INGEST_URL, blob)
        if (!ok) queue = [...batch, ...queue].slice(-MAX_QUEUE)
    } catch {
        queue = [...batch, ...queue].slice(-MAX_QUEUE)
    }
}

/**
 * Arranca el cliente una sola vez. Emite `app_open`, captura la versión del
 * shell (solo shell), y conecta los hooks de page-hide que emiten `app_close`
 * (con la duración de la corrida) y drenan el buffer.
 */
export function initAnalytics() {
    if (initialized || !browser() || !isTelemetryEnabled()) return
    initialized = true
    sessionId = (() => {
        try {
            return crypto.randomUUID()
        } catch {
            return `s-${Date.now()}`
        }
    })()
    startedAt = Date.now()

    // La versión del shell es async y solo existe ahí; se cachea antes del
    // primer flush.
    getBridge()
        ?.getAppVersion?.()
        .then((v) => {
            shellVersion = v && v !== 'web' ? v : null
        })
        .catch(() => {
            /* mock de navegador / no disponible */
        })

    track('app_open', deviceContext())

    // Ocultarse (minimizar / cambiar de tab) NO es el fin de la corrida — solo
    // drena lo bufferizado para que un crash posterior no lo pierda. No emite
    // app_close acá o una sola corrida logueraría muchos cierres.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushBeacon()
    })
    // pagehide es la señal confiable de "nos vamos de verdad" en el webview
    // del shell — emite la duración de la corrida una vez y drena.
    let closed = false
    window.addEventListener('pagehide', () => {
        if (closed) return
        closed = true
        track('app_close', { durationMs: Date.now() - startedAt })
        flushBeacon()
    })
}
